import { Helmet } from 'react-helmet-async';
import { Button } from 'utils/Button';
import ReusableSectionTwo from 'components/ReusableSectionTwo';
import { aboutPeopleData } from '../data/About';
import Breadcrumbs from 'components/Breadcrumbs';
import { useLocation } from 'react-router-dom';

const banner =
  'https://res.cloudinary.com/dwoubdppz/image/upload/v1751597877/oak-and-stone-remodel/kitchen/kitchen33.webp';

export const About = () => {
  const location = useLocation();
  return (
    <div>
      {/* SEO Metadata */}
      <Helmet>
        <title>
          About Oak & Stone Construction | Trusted Home Remodelers in Utah
        </title>
        <meta
          name="description"
          content="Discover the craftsmanship and passion behind Oak & Stone Construction. With 35+ years of experience, we provide top-quality home remodeling in Utah. Learn more about our team and expertise."
        />
        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="About Oak & Stone Construction | Trusted Home Remodelers in Utah"
        />
        <meta
          property="og:description"
          content="Discover the craftsmanship and passion behind Oak & Stone Construction. With 35+ years of experience, we provide top-quality home remodeling in Utah. Learn more about our team and expertise."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597877/oak-and-stone-remodel/kitchen/kitchen33.webp"
        />
        <meta
          property="og:url"
          content="https://oakandstoneremodel.com/about"
        />
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="About Oak & Stone Construction | Trusted Home Remodelers in Utah"
        />
        <meta
          name="twitter:description"
          content="Discover the craftsmanship and passion behind Oak & Stone Construction. With 35+ years of experience, we provide top-quality home remodeling in Utah. Learn more about our team and expertise."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597877/oak-and-stone-remodel/kitchen/kitchen33.webp"
        />
        <meta
          name="twitter:url"
          content="https://oakandstoneremodel.com/about"
        />
        <link rel="canonical" href="https://oakandstoneremodel.com/about" />
      </Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'About Oak & Stone Construction | Trusted Home Remodelers in Utah',
          url: 'https://oakandstoneremodel.com/about',
          description:
            'Discover the craftsmanship and passion behind Oak & Stone Construction. With 35+ years of experience, we provide top-quality home remodeling in Utah. Learn more about our team and expertise.'
        })}
      </script>

      {/* Page Header */}
      <div className="h-[45vh] w-full relative">
        <div className="absolute top-0 left-0 z-10 w-full h-full">
          <div className="relative grid content-center w-full h-full ">
            <h1 className="text-4xl text-center text-white uppercase">
              About us
            </h1>

            <div className="flex flex-wrap justify-center mx-auto mt-6 space-y-4 xs:space-y-0 xs:space-x-6">
              <Button to="/gallery" text="VIEW PORTFOLIO" classname="" />
              <Button to="/contact" text="REQUEST CONSULTATION" classname="" />
            </div>
          </div>
        </div>
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-[35%]"></div>
        <img
          src={banner}
          className="flex-shrink-0 object-cover w-full h-full bg-no-repeat"
          alt="A stunning kitchen remodel by Oak and Stone featuring custom cabinetry, elegant countertops, and modern fixtures"
        />
        <Breadcrumbs pathname={location.pathname} />
      </div>

      {/* About Content */}
      <ReusableSectionTwo
        title="Meet the Craftsmen Behind Oak and Stone: A Legacy of Precision and Passion"
        subtitle="We Want to Earn Your Business & Gain Your Referrals"
        content={[
          'Oak & Stone Construction, Inc. has been a trusted name in the greater Wasatch area for over 35 years. Since 1990, we have built a reputation for delivering high-quality craftsmanship and professional results. We take pride in our skilled team and reliable subcontractors, ensuring every project meets our exacting standards. Customer satisfaction is at the heart of what we do—we want you to love every aspect of your project.',
          'Our commitment to service has earned us loyal clients, repeat business, and confident referrals. We stand by our work, addressing any concerns promptly to uphold our reputation for excellence.'
        ]}
        buttonText="Request Consultation"
        buttonLink="/contact"
      />

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The passionate craftsmen and professionals who bring your vision
              to life with precision, dedication, and years of expertise.
            </p>
          </div>

          {/* Team Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aboutPeopleData.map((member, index) => (
              <div
                key={member.name}
                className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Image Container */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {member.name}
                  </h3>
                  <div className="space-y-4">
                    {member.text.map((paragraph, textIndex) => (
                      <p
                        key={textIndex}
                        className="text-gray-600 leading-relaxed text-sm">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Work With Our Team?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Let our experienced craftsmen transform your space with the same
                dedication and precision that has made Oak & Stone a trusted
                name in Utah for over 35 years.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  to="/contact"
                  text="Schedule Consultation"
                  classname="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                />
                <Button
                  to="/gallery"
                  text="View Our Work"
                  classname="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
